import { Table } from "antd";
import "./DataTable.scss";
import { ReactNode, useEffect } from "react";
import { useIntl } from "react-intl";
import { DataTableActions } from "~components/DataTable/components/DataTableActions/DataTableActions.tsx";
import { DataTableHeader } from "~components/DataTable/components/DataTableHeader/DataTableHeader.tsx";
import type { PermissionNames } from "~enums/permissions.ts";
import { useTableColumns } from "./hooks/useTableColumns.ts";
import { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { FiltersConfig } from "~components/DataTable/types/filtersConfig.ts";
import { ImportConfig } from "~components/DataTable/types/importConfig.ts";
import { ExportConfig } from "~components/DataTable/types/exportConfig.ts";
import { PaginationConfig } from "~components/DataTable/types/paginationConfig.ts";
import { useAppSettings } from "~context/AppSettingsContext/AppSettingsContext.tsx";
import { DataTableConfig } from "~types/dataTableConfig.ts";
import { SearchConfig } from "~components/DataTable/types/searchConfig.ts";
import { useLocation } from "react-router-dom";

type SortConfig = {
  sorterLocalStorageKey: string;
  defaultSorter: { columnKey: string; order: "ascend" | "descend" };
};

type Props<T = unknown> = {
  columns: DataTableColumn<T>[];
  rowKey: string;
  dataSource: T[];
  title?: ReactNode;
  columnChooserLocalStorageKey: string;
  columnSortConfig: SortConfig;
  isDataLoading?: boolean;
  permissionNames: Array<PermissionNames>;
  filtersConfig?: FiltersConfig;
  searchConfig?: SearchConfig;
  searchPlaceholderIntlKey?: string;
  handleAdd?: () => void;
  addButtonIntlKey: string;
  importConfig?: ImportConfig;
  handleRefresh?: () => void;
  exportConfig?: ExportConfig;
  paginationConfig?: PaginationConfig;
  onMassEditModeChangedHandler?: (checked: boolean) => void;
  dataTableUiConfiguration: DataTableConfig;
};

export const DataTable = <T extends object = any>({
  columns,
  columnSortConfig,
  dataSource,
  searchConfig,
  handleAdd,
  permissionNames,
  addButtonIntlKey,
  searchPlaceholderIntlKey,
  importConfig,
  columnChooserLocalStorageKey,
  title,
  exportConfig,
                                                    filtersConfig,
  handleRefresh,
  onMassEditModeChangedHandler,
  paginationConfig,
  isDataLoading,
  rowKey,
                                                    dataTableUiConfiguration
}: Props<T>) => {
  const tableColumns = columns.map((column) => ({ ...column, key: column.key || column.dataIndex })) as DataTableColumn<T>[];
  const { formatMessage } = useIntl();
  const { tableDensity } = useAppSettings();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.search) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.search]);

  const { columnsToShow, setSelectedColumns, setSortColumn } = useTableColumns<T>({
    columnChooserConfig: {
      columnChooserLocalStorageKey: columnChooserLocalStorageKey,
      tableConfiguration: dataTableUiConfiguration.columnsConfig,
    },
    columnSortConfig: columnSortConfig,
    tableColumns: tableColumns,
  });

  return (
    <div className="data-table-container">
      <div className="data-table-container-header">
        {title && <div className="data-table-title">{title}</div>}
        <DataTableActions
          exportConfig={exportConfig}
          importConfig={importConfig}
          handleAdd={handleAdd}
          permissionNames={permissionNames}
          addButtonIntlKey={addButtonIntlKey}
        />
      </div>
      <DataTableHeader
        filtersConfig={filtersConfig}
        handleAdd={handleAdd}
        searchConfig={searchConfig}
        permissionNames={permissionNames}
        addButtonIntlKey={addButtonIntlKey}
        tableColumns={tableColumns}
        setSelectedColumns={setSelectedColumns}
        columnChooserLocalStorageKey={columnChooserLocalStorageKey}
        dataTableUiConfiguration={dataTableUiConfiguration}
        searchPlaceholderIntlKey={searchPlaceholderIntlKey}
        onMassEditModeChangedHandler={onMassEditModeChangedHandler}
        handleRefresh={handleRefresh}
      />
      <Table<T>
        columns={columnsToShow}
        rowKey={rowKey}
        onChange={(_, __, sorter) => setSortColumn(sorter)}
        dataSource={dataSource}
        scroll={{ x: "max-content" }}
        sticky={true}
        loading={isDataLoading}
        tableLayout="fixed"
        size={tableDensity}
        pagination={{
          current: paginationConfig?.currentPage,
          pageSize: paginationConfig?.pageSize,
          onChange: paginationConfig?.handlePaginationChange,
          pageSizeOptions: paginationConfig?.pageSizeOptions,
          rootClassName: "data-table-pagination",
          showSizeChanger: true,
          showTitle: false,
          showTotal: () => formatMessage({ id: "data.table.pagination.display" }),
          itemRender: (_, type, originalElement) => {
            if (type === "next") {
              return <i className="fa-solid fa-chevron-right" style={{ fontSize: 14 }}></i>;
            }
            if (type === "prev") {
              return <i className="fa-solid fa-chevron-left"></i>;
            }
            return originalElement;
          },
        }}
      />
    </div>
  );
};
